import { reactive } from 'vue';

export enum LogStatus {
  INFO = 'INFO',
  WARN = 'WARN',
  ERROR = 'ERROR',
  SUCCESS = 'SUCCESS',
  BOLD = 'BOLD',
}

export interface Log {
  id: number;
  status: LogStatus;
  message: string;
}

export class ProgressLog implements Log {
  public id: number;
  public status: LogStatus;
  public message: string;

  public size: number;
  public prefix: string;

  constructor(id: number, prefix: string, size: number = 12) {
    this.id = id;
    this.prefix = prefix;
    this.status = LogStatus.INFO;
    this.size = size;
    this.message = `${this.prefix}: `;
    this.setProgress(0);
  }

  public setProgress(percent: number) {
    const progress = Math.round(this.size * percent);
    const emptyProgress = Math.round(this.size - progress);
    const progressText = '▇'.repeat(progress);
    const emptyProgressText = '—'.repeat(emptyProgress);
    const percentageText = Math.round(percent * 100) + '%';

    this.message = `${this.prefix}: [${progressText}${emptyProgressText}] ${percentageText}`;
  }

  public complete() {
    this.message = `${this.prefix} complete`;
  }
}

export class CommandLog implements Log {
  public id: number;
  public status: LogStatus;
  public message: string;

  constructor(id: number, command: string) {
    this.id = id;
    this.status = LogStatus.BOLD;
    this.message = `> ${command}`;
  }
}

export class ResultLog implements Log {
  public id: number;
  public status: LogStatus;
  public message: string;

  constructor(id: number, message: string, status: LogStatus) {
    this.id = id;
    this.status = status;
    this.message = message;
  }
}

export class Logger {
  private _logs: Array<Log>;

  constructor(logs: Array<Log>) {
    this._logs = logs;
  }

  public logCommand(command: string): CommandLog {
    const commandLog = reactive(new CommandLog(this._logs.length, command));
    this._logs.push(commandLog);
    return commandLog;
  }

  public logResult(msg: string, status: LogStatus): ResultLog {
    const resultLog = reactive(new ResultLog(this._logs.length, msg, status));
    this._logs.push(resultLog);
    return resultLog;
  }

  public logProgress(title: string): ProgressLog {
    const progressLog = reactive(new ProgressLog(this._logs.length, title));
    this._logs.push(progressLog);
    return progressLog;
  }
}
